import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Typography,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  InputAdornment,
} from '@mui/material';
import { Add, Edit, Delete, Link, LinkOff, Search } from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Motorista, Veiculo } from '../types';
import { motoristaService } from '../services/motoristaService';
import { veiculoService } from '../services/veiculoService';
import { getErrorMessage } from '../utils/errors';
import ConfirmDialog from '../components/ConfirmDialog';
import DateMaskField from '../components/DateMaskField';

const schema = yup.object({
  nome: yup.string().required('Nome é obrigatório'),
  cpf: yup.string().required('CPF é obrigatório').matches(/^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/, 'CPF inválido'),
  cnh: yup.string().required('CNH é obrigatória'),
  categoriaCnh: yup.string().required('Categoria da CNH é obrigatória'),
  validadeCnh: yup.string().required('Validade da CNH é obrigatória'),
  dataNascimento: yup.string().required('Data de nascimento é obrigatória'),
  telefone: yup.string().required('Telefone é obrigatório'),
  email: yup.string().required('Email é obrigatório').email('Email inválido'),
}).required();

const motoristaVazio = {
  nome: '',
  cpf: '',
  cnh: '',
  categoriaCnh: '',
  validadeCnh: '',
  dataNascimento: '',
  telefone: '',
  email: '',
};

const Motoristas: React.FC = () => {
  const [motoristas, setMotoristas] = useState<Motorista[]>([]);
  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [veiculosAlocados, setVeiculosAlocados] = useState<Record<number, Veiculo[]>>({});
  const [open, setOpen] = useState(false);
  const [openAlocacao, setOpenAlocacao] = useState(false);
  const [editingMotorista, setEditingMotorista] = useState<Motorista | null>(null);
  const [motoristaSelecionado, setMotoristaSelecionado] = useState<Motorista | null>(null);
  const [veiculoParaAlocar, setVeiculoParaAlocar] = useState<number>(0);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [senhaGerada, setSenhaGerada] = useState<{ email: string; senha: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });
  const [filtro, setFiltro] = useState('');

  const { register, control, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    loadMotoristas();
    loadVeiculos();
  }, []);

  const loadMotoristas = async () => {
    try {
      const data = await motoristaService.listar();
      setMotoristas(data);
    } catch (error) {
      console.error('Erro ao carregar motoristas', error);
    }
  };

  const loadVeiculos = async () => {
    try {
      const data = await veiculoService.listarPorStatus('ATIVO');
      setVeiculos(data);
    } catch (error) {
      console.error('Erro ao carregar veículos', error);
    }
  };

  const handleOpen = (motorista?: Motorista) => {
    setErro('');
    setSenhaGerada(null);
    if (motorista) {
      setEditingMotorista(motorista);
      reset(motorista);
    } else {
      setEditingMotorista(null);
      reset(motoristaVazio);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingMotorista(null);
    reset(motoristaVazio);
    setErro('');
    setSenhaGerada(null);
  };

  const onSubmit = async (data: any) => {
    setSalvando(true);
    try {
      if (editingMotorista?.id) {
        await motoristaService.atualizar(editingMotorista.id, data);
        loadMotoristas();
        handleClose();
      } else {
        const resultado = await motoristaService.cadastrar(data);
        const email = data.email;
        const senha = resultado.dados?.senhaGerada || '';
        loadMotoristas();
        setOpen(false);
        setEditingMotorista(null);
        reset(motoristaVazio);
        setSenhaGerada({ email, senha });
      }
    } catch (error) {
      setErro(getErrorMessage(error));
    } finally {
      setSalvando(false);
    }
  };

  const handleDelete = (id: number) => {
    setConfirmDelete({ open: true, id });
  };

  const confirmDeleteAction = async () => {
    if (!confirmDelete.id) return;
    try {
      await motoristaService.deletar(confirmDelete.id);
      loadMotoristas();
      setConfirmDelete({ open: false, id: null });
    } catch (error) {
      console.error('Erro ao deletar motorista', error);
      setConfirmDelete({ open: false, id: null });
    }
  };

  const handleOpenAlocacao = async (motorista: Motorista) => {
    setMotoristaSelecionado(motorista);
    setVeiculoParaAlocar(0);
    setErro('');
    setOpenAlocacao(true);
  };

  const handleAlocar = async () => {
    if (!motoristaSelecionado?.id || !veiculoParaAlocar) return;
    setSalvando(true);
    try {
      await motoristaService.alocarVeiculo(motoristaSelecionado.id, veiculoParaAlocar);
      const atualizado = await motoristaService.buscarPorId(motoristaSelecionado.id);
      setMotoristaSelecionado(atualizado);
      setVeiculoParaAlocar(0);
      loadMotoristas();
    } catch (error) {
      setErro(getErrorMessage(error));
    } finally {
      setSalvando(false);
    }
  };

  const handleDesalocar = async (veiculoId: number) => {
    if (!motoristaSelecionado?.id) return;
    try {
      await motoristaService.desalocarVeiculo(motoristaSelecionado.id, veiculoId);
      const atualizado = await motoristaService.buscarPorId(motoristaSelecionado.id);
      setMotoristaSelecionado(atualizado);
      loadMotoristas();
    } catch (error) {
      setErro(getErrorMessage(error));
    }
  };

  const termo = filtro.trim().toLowerCase();
  const motoristasFiltrados = termo
    ? motoristas.filter((item) => Object.values(item).filter(Boolean).some((campo) => String(campo).toLowerCase().includes(termo)))
    : motoristas;

  return (
    <Container disableGutters maxWidth={false}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">Motoristas</Typography>
        <Box display="flex" gap={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Pesquisar"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ minWidth: 260 }}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
          >
            Novo Motorista
          </Button>
        </Box>
      </Box>

      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>CPF</TableCell>
              <TableCell>Telefone</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Veículos Alocados</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {motoristasFiltrados.map((motorista) => (
              <TableRow key={motorista.id}>
                <TableCell><strong>{motorista.nome}</strong></TableCell>
                <TableCell>{motorista.cpf}</TableCell>
                <TableCell>{motorista.telefone}</TableCell>
                <TableCell sx={{ maxWidth: 320, minWidth: 200, wordBreak: 'break-word' }}>{motorista.email}</TableCell>
                <TableCell>
                  {motorista.veiculosAlocados && motorista.veiculosAlocados.length > 0 ? (
                    motorista.veiculosAlocados.map((v) => (
                      <Chip key={v.id} label={v.placa} size="small" color="primary" sx={{ mr: 0.5, mb: 0.5 }} />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">Nenhum</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={motorista.ativo ? 'Ativo' : 'Inativo'}
                    color={motorista.ativo ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    color="primary"
                    title="Gerenciar veículos"
                    onClick={() => handleOpenAlocacao(motorista)}
                  >
                    <Link />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleOpen(motorista)}>
                    <Edit />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => motorista.id && handleDelete(motorista.id)}>
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de Cadastro/Edição */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingMotorista ? 'Editar Motorista' : 'Novo Motorista'}</DialogTitle>
        <DialogContent>
      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
      {senhaGerada && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSenhaGerada(null)}>
          <Typography variant="subtitle2">Motorista cadastrado com sucesso!</Typography>
          <Typography variant="body2">
            <strong>Email:</strong> {senhaGerada.email} | <strong>Senha:</strong> <code>{senhaGerada.senha}</code>
          </Typography>
        </Alert>
      )}
          <form id="motorista-form" onSubmit={handleSubmit(onSubmit as any)}>
            <TextField
              fullWidth
              label="Nome"
              {...register('nome')}
              error={!!errors.nome}
              helperText={errors.nome?.message}
              margin="normal"
            />
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', '& > *': { flex: '1 1 0', minWidth: 0 } }}>
              <TextField
                fullWidth
                label="CPF"
                {...register('cpf')}
                error={!!errors.cpf}
                helperText={errors.cpf?.message}
                placeholder="000.000.000-00"
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
              <Controller
                name="dataNascimento"
                control={control}
                render={({ field }) => (
                  <DateMaskField
                    label="Data de Nascimento"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    inputRef={field.ref}
                    error={!!errors.dataNascimento}
                    helperText={errors.dataNascimento?.message}
                  />
                )}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', '& > *': { flex: '1 1 0', minWidth: 0 } }}>
              <TextField
                fullWidth
                label="CNH"
                {...register('cnh')}
                error={!!errors.cnh}
                helperText={errors.cnh?.message}
                margin="normal"
              />
              <Controller
                name="validadeCnh"
                control={control}
                render={({ field }) => (
                  <DateMaskField
                    label="Validade da CNH"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    inputRef={field.ref}
                    error={!!errors.validadeCnh}
                    helperText={errors.validadeCnh?.message}
                  />
                )}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', '& > *': { flex: '1 1 0', minWidth: 0 } }}>
              <Controller
                name="categoriaCnh"
                control={control}
                render={({ field }) => (
                  <TextField
                    select
                    fullWidth
                    label="Categoria da CNH"
                    name={field.name}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    error={!!errors.categoriaCnh}
                    helperText={errors.categoriaCnh?.message}
                    margin="normal"
                  >
                    {['A', 'B', 'C', 'D', 'E', 'AB', 'AC', 'AD', 'AE'].map((cat) => (
                      <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                    ))}
                  </TextField>
                )}
              />
              <TextField
                fullWidth
                label="Telefone"
                {...register('telefone')}
                error={!!errors.telefone}
                helperText={errors.telefone?.message}
                placeholder="(00) 00000-0000"
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <TextField
              fullWidth
              label="Email"
              type="email"
              {...register('email')}
              error={!!errors.email}
              helperText={errors.email?.message}
              margin="normal"
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button type="submit" form="motorista-form" variant="contained" disabled={salvando}>
            {salvando ? 'Salvando...' : (editingMotorista ? 'Atualizar' : 'Cadastrar')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Alocação de Veículos */}
      <Dialog open={openAlocacao} onClose={() => setOpenAlocacao(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Alocação de Veículos - {motoristaSelecionado?.nome}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {motoristaSelecionado?.veiculosAlocados && motoristaSelecionado.veiculosAlocados.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Veículos Alocados:
                </Typography>
                <List dense>
                  {motoristaSelecionado.veiculosAlocados.map((v) => (
                    <ListItem key={v.id} sx={{ bgcolor: 'grey.50', borderRadius: 1, mb: 0.5 }}>
                      <ListItemText
                        primary={`${v.placa} - ${v.marca} ${v.modelo}`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDesalocar(v.id)}
                          title="Desalocar"
                        >
                          <LinkOff />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {(!motoristaSelecionado?.veiculosAlocados || motoristaSelecionado.veiculosAlocados.length === 0) && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Nenhum veículo alocado.
              </Typography>
            )}

            <Typography variant="subtitle2" gutterBottom>
              Alocar novo veículo:
            </Typography>
            <Box display="flex" gap={1} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                fullWidth
                select
                label="Selecione o veículo"
                value={veiculoParaAlocar}
                onChange={(e) => setVeiculoParaAlocar(Number(e.target.value))}
                size="small"
              >
                <MenuItem value="">Selecione...</MenuItem>
                {veiculos
                  .filter((v) => !motoristaSelecionado?.veiculosAlocados?.some((a) => a.id === v.id))
                  .map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.placa} - {v.marca} {v.modelo}
                    </MenuItem>
                  ))}
              </TextField>
              <Button
                variant="contained"
                onClick={handleAlocar}
                disabled={!veiculoParaAlocar || salvando}
                startIcon={<Link />}
                sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
              >
                {salvando ? 'Alocando...' : 'Alocar'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAlocacao(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDelete.open}
        title="Confirmar desativação"
        message="Tem certeza que deseja desativar este motorista? Ele não poderá mais acessar o sistema."
        confirmText="Desativar"
        severity="error"
        onConfirm={confirmDeleteAction}
        onCancel={() => setConfirmDelete({ open: false, id: null })}
      />
    </Container>
  );
};

export default Motoristas;
